import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { BrandBar } from "@/components/landing/BrandBar";
import api, { formatApiError } from "@/lib/api";

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z"/>
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.92l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z"/>
    <path fill="#FBBC05" d="M5.27 14.27A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.27v-3.1H1.29a12 12 0 0 0 0 10.74l3.98-3.1z"/>
    <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.63l3.98 3.1C6.22 6.88 8.87 4.77 12 4.77z"/>
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.error || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((res) => {
      const role = res.data?.role;
      navigate(role === "member" ? "/training" : role === "manager" ? "/company" : "/admin");
    }).catch(() => {});
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      const role = res.data?.role;
      navigate(role === "member" ? "/training" : role === "manager" ? "/company" : "/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls =
    "w-full rounded-md border border-slate-200 bg-[#EDF4FB] px-4 py-3 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/60 transition-all";

  return (
    <div className="min-h-screen workspace-light bg-[#EDF4FB] flex items-center justify-center px-4 relative" data-testid="login-page">
      <BrandBar />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex justify-center mb-8">
          <Link to="/" aria-label="Back to OPERA360 home">
            <img
              src="/logo-full.png"
              alt="OPERA360 — The Operating System for Property Performance"
              className="h-44 w-auto rounded-md bg-white p-4 shadow-2xl"
              data-testid="login-logo"
            />
          </Link>
        </div>
        <form onSubmit={submit} className="technical-card p-8" data-testid="login-form">
          <div className="flex items-center gap-2.5 mb-1">
            <Lock size={14} className="text-sky-600" />
            <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">Team Access</p>
          </div>
          <p className="text-sm text-muted-foreground mb-7">Sign in to your OPERA360 workspace — CRM, portal, and training.</p>

          <button
            type="button"
            data-testid="login-google-button"
            onClick={googleLogin}
            className="inline-flex w-full items-center justify-center gap-2.5 rounded-md border border-slate-300 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-900 transition-all duration-200 hover:border-white/30 hover:bg-white/[0.06] active:scale-[0.98]"
          >
            <GoogleIcon /> Sign in with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/60">or with email</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <div className="space-y-4">
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@opera360.com"
              className={inputCls}
            />
            <input
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className={inputCls}
            />
          </div>
          {error && (
            <p data-testid="login-error-message" className="mt-4 text-sm text-red-600 font-medium">{error}</p>
          )}
          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-cadmium px-5 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-cadmium-hover active:scale-[0.98] disabled:opacity-60"
          >
            {loading && <Loader2 size={15} className="animate-spin" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <p className="mt-6 text-center">
          <Link to="/" className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-sky-600 transition-colors">
            ← Back to site
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
